import {useState, useEffect} from "react";
import AgoraRTC, {
    IAgoraRTCClient,
    IAgoraRTCRemoteUser,
    MicrophoneAudioTrackInitConfig,
    CameraVideoTrackInitConfig,
    IMicrophoneAudioTrack,
    ICameraVideoTrack,
    ILocalVideoTrack,
    ILocalAudioTrack
} from "agora-rtc-sdk-ng";
import {getCookie} from "../helpers/cookieHelpers";
import {useSelector} from "react-redux";
import {RootState} from "../redux/store";
import * as Sentry from "@sentry/react";

export default function useAgora(client: IAgoraRTCClient | undefined)
    :
    {
        localAudioTrack: IMicrophoneAudioTrack | undefined,
        localVideoTrack: ICameraVideoTrack | undefined,
        joinState: boolean,
        leave: Function,
        join: Function,
        remoteUsers: IAgoraRTCRemoteUser[],
        audioDevices: object[] | any,
        videoDevices: object[] | any,
        audioDevice: object | any,
        videoDevice: object | any,
        updateAudioInput: (string) => void,
        updateVideoInput: (string) => void,
    } {
    const [localVideoTrack, setLocalVideoTrack] = useState<ICameraVideoTrack | undefined>(undefined);
    const [localAudioTrack, setLocalAudioTrack] = useState<IMicrophoneAudioTrack | undefined>(undefined);
    const [audioDevices, setAudioDevices] = useState([]);
    const [videoDevices, setVideoDevices] = useState([]);
    const [audioDevice, setAudioDevice] = useState(null);
    const [videoDevice, setVideoDevice] = useState(null);
    const [joinState, setJoinState] = useState(undefined);

    const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);

    const initialAccessState = {
        location: true,
        camera: true,
        microphone: true,
    };
    const [accessData, setAccessData] = useState(initialAccessState);
    const {role} = useSelector((state: RootState) => state?.user);
    const isOwner = role === "OWNER";

    useEffect(() => {
        const accessData = localStorage.getItem("accessData");
        if (accessData === null) {
            localStorage.setItem("accessData", JSON.stringify(initialAccessState));
        } else {
            setAccessData(JSON.parse(localStorage.getItem("accessData")));
        }
        return (() => {
            leave();
        });
    }, []);

    async function createLocalCameraTrack(audioConfig?: MicrophoneAudioTrackInitConfig, videoConfig?: CameraVideoTrackInitConfig) {
        if (isOwner && !accessData?.camera) return;
        try {
            return AgoraRTC.getCameras()
                .then(devices => {
                    const videoDevices = devices.filter(function (device) {
                        return device.kind === "videoinput";
                    })
                        .map(({deviceId, label}, idx) => ({
                            deviceId,
                            label: label || `Camera ${idx}`
                        }));
                    setVideoDevices(videoDevices);
                    const savedData = JSON.parse(getCookie("room"));
                    const selectedCameraId = savedData? videoDevices.find(({deviceId}) => deviceId === savedData?.video)?.deviceId : videoDevices[0].deviceId;
                    setVideoDevice(selectedCameraId);
                    return AgoraRTC.createCameraVideoTrack({cameraId: selectedCameraId});
                })
                .then((cameraTrack) => {
                    setLocalVideoTrack(cameraTrack);
                    return cameraTrack;
                });
        } catch (err) {
            Sentry.captureException(err);
        }
    }

    async function createLocalMicrophoneTrack(audioConfig?: MicrophoneAudioTrackInitConfig, videoConfig?: CameraVideoTrackInitConfig) {
        if (isOwner && !accessData?.microphone) return;
        try {
            return AgoraRTC.getMicrophones()
                .then(devices => {
                    const audioDevices = devices.filter(function (device) {
                        return device.kind === "audioinput";
                    })
                        .map(({deviceId, label}, idx) => ({
                            deviceId,
                            label: label || `Microphone ${idx}`
                        }));
                    setAudioDevices(audioDevices);

                    const savedData = JSON.parse(getCookie("room"));
                    const selectedMicrophoneId = savedData ? audioDevices.find(({deviceId}) => deviceId === savedData?.audio)?.deviceId : audioDevices[0].deviceId;
                    setAudioDevice(selectedMicrophoneId);
                    return AgoraRTC.createMicrophoneAudioTrack({microphoneId: selectedMicrophoneId});
                })
                .then((microphoneTrack) => {
                    setLocalAudioTrack(microphoneTrack);
                    return microphoneTrack;
                });
        } catch (err) {
            Sentry.captureException(err);
        }

    }

    const updateAudioInput = (id) => {
        try {
            if (localAudioTrack) {
                localAudioTrack.setDevice(id).then(() => {
                    setAudioDevice(id);
                    console.log("set device success");
                }).catch(e => {
                    console.log("set device error", e);
                });
            }
        } catch (e) {
            Sentry.captureException(e);
        }
    };

    const updateVideoInput = (id) => {
        try {
            if (localVideoTrack) {
                localVideoTrack.setDevice(id).then(() => {
                    setVideoDevice(id);
                    console.log("set device success");
                }).catch(e => {
                    console.log("set device error", e);
                });
            }
        } catch (e) {
            Sentry.captureException(e);
        }
    };

    async function join(appid: string, channel: string, token?: string, uid?: string | number | null) {
        if (!client) return;
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
            const cameraTrack = await createLocalCameraTrack();
            const microphoneTrack = await createLocalMicrophoneTrack();

            await client.join(appid, channel, token || null, uid || null);
            await client.publish([microphoneTrack, cameraTrack]);

            (window as any).client = client;
            (window as any).videoTrack = cameraTrack;
            setJoinState(true);
        } catch(e) {
            setJoinState(false);
            Sentry.captureException(e);
        }
    }

    async function leave() {
        try {
            if (localAudioTrack) {
                localAudioTrack.stop();
                localAudioTrack.close();
            }
            if (localVideoTrack) {
                localVideoTrack.stop();
                localVideoTrack.close();
            }
            setRemoteUsers([]);
            setJoinState(false);
            await client?.leave();
        } catch (e) {
            Sentry.captureException(e);
        }
    }

    useEffect(() => {
        if (!client) return;
        setRemoteUsers(client.remoteUsers);

        const handleUserPublished = async (user: IAgoraRTCRemoteUser, mediaType: "audio" | "video") => {
            await client.subscribe(user, mediaType);
            // toggle rerender while state of remoteUsers changed.
            setRemoteUsers(remoteUsers => Array.from(client.remoteUsers));
        };
        const handleUserUnpublished = (user: IAgoraRTCRemoteUser) => {
            setRemoteUsers(remoteUsers => Array.from(client.remoteUsers));
        };
        const handleUserJoined = (user: IAgoraRTCRemoteUser) => {
            setRemoteUsers(remoteUsers => Array.from(client.remoteUsers));
        };
        const handleUserLeft = (user: IAgoraRTCRemoteUser) => {
            setRemoteUsers(remoteUsers => Array.from(client.remoteUsers));
        };
        client.on("user-published", handleUserPublished);
        client.on("user-unpublished", handleUserUnpublished);
        client.on("user-joined", handleUserJoined);
        client.on("user-left", handleUserLeft);

        return () => {
            client.off("user-published", handleUserPublished);
            client.off("user-unpublished", handleUserUnpublished);
            client.off("user-joined", handleUserJoined);
            client.off("user-left", handleUserLeft);
        };
    }, [client]);

    return {
        localAudioTrack,
        localVideoTrack,
        joinState,
        leave,
        join,
        remoteUsers,
        audioDevices,
        videoDevices,
        audioDevice,
        videoDevice,
        updateAudioInput,
        updateVideoInput,
    };
}
